import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PulseStrip from "@/components/PulseStrip";
import CommunityRulesModal from "@/components/CommunityRulesModal";

export default function AuthPage() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/lobby";
  const [mode, setMode] = useState("guest"); // guest | login | register
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", password: "", gender: "" });
  const [rulesOpen, setRulesOpen] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if ((mode === "guest" || mode === "register") && !form.gender) {
      toast.error("Please select male or female.");
      return;
    }
    setBusy(true);
    try {
      let res;
      if (mode === "guest") {
        res = await api.post("/auth/guest", { username: form.username, gender: form.gender });
      } else if (mode === "login") {
        res = await api.post("/auth/login", { email: form.email, password: form.password });
      } else {
        res = await api.post("/auth/register", {
          username: form.username,
          email: form.email,
          password: form.password,
          gender: form.gender,
        });
      }
      setSession(res.data.token, res.data.user);
      toast.success(`WELCOME, ${res.data.user.username.toUpperCase()}`);
      nav(nextPath);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row">
      {/* Left panel — terminal prompt */}
      <div className="md:w-1/2 border-b md:border-b-0 md:border-r border-white/10 p-8 md:p-16 flex flex-col justify-between">
        <div>
          <div className="font-mono text-acid text-xs tracking-widest">// NW.TERMINAL</div>
          <h1 className="font-heading text-6xl md:text-8xl leading-none mt-4">
            DROP<br />
            <span className="text-acid">IN.</span>
          </h1>
          <p className="font-mono text-white/60 text-xs md:text-sm mt-6 max-w-md">
            &gt; Pick a handle. Pick a room. It&apos;s already loud in there.
            <span className="text-acid animate-blink">_</span>
          </p>
        </div>
        <div className="mt-10 font-mono text-white/40 text-[10px] tracking-widest space-y-1">
          <div>[STATUS] LIVE</div>
          <div>[ROOMS] 8+ ALWAYS OPEN</div>
          <div>[RULES] BE KIND · NO HATE · REPORT BUTTON EXISTS</div>
        </div>

        <div className="mt-8">
          <PulseStrip compact />
        </div>
      </div>

      {/* Right panel — form */}
      <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center bg-surface">
        <div className="flex gap-0 mb-8" data-testid="auth-tabs">
          {["guest", "login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              data-testid={`tab-${m}`}
              className={`flex-1 font-mono text-xs tracking-widest uppercase py-3 border-2 border-white/20 ${
                mode === m ? "bg-acid text-obsidian border-acid" : "text-white/70 hover:text-white"
              }`}
            >
              {m === "guest" ? "GUEST" : m === "login" ? "LOG IN" : "REGISTER"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {(mode === "guest" || mode === "register") && (
            <div>
              <label className="font-mono text-[10px] tracking-widest text-white/60">HANDLE</label>
              <input
                data-testid="input-username"
                className="input-brutal mt-1"
                placeholder="e.g. midnight_owl"
                value={form.username}
                onChange={update("username")}
                required
                minLength={3}
                maxLength={24}
              />
            </div>
          )}
          {(mode === "guest" || mode === "register") && (
            <div>
              <label className="font-mono text-[10px] tracking-widest text-white/60">I AM</label>
              <div className="mt-1 grid grid-cols-2 gap-0" data-testid="gender-select">
                {[
                  { value: "male", label: "♂ MALE" },
                  { value: "female", label: "♀ FEMALE" },
                ].map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    data-testid={`gender-${g.value}`}
                    onClick={() => setForm((f) => ({ ...f, gender: g.value }))}
                    className={`font-mono text-xs tracking-widest uppercase py-3 border-2 border-white/20 transition-colors ${
                      form.gender === g.value
                        ? "bg-acid text-obsidian border-acid"
                        : "text-white/70 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(mode === "login" || mode === "register") && (
            <>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/60">EMAIL</label>
                <input
                  data-testid="input-email"
                  type="email"
                  className="input-brutal mt-1"
                  placeholder="you@somewhere.net"
                  value={form.email}
                  onChange={update("email")}
                  required
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/60">PASSWORD</label>
                <input
                  data-testid="input-password"
                  type="password"
                  className="input-brutal mt-1"
                  placeholder="min 6 chars"
                  value={form.password}
                  onChange={update("password")}
                  required
                  minLength={6}
                />
              </div>
            </>
          )}

          <button data-testid="submit-auth" className="btn-acid w-full mt-6" disabled={busy}>
            {busy ? "CONNECTING…" : mode === "guest" ? "DROP IN AS GUEST" : mode === "login" ? "LOG IN" : "CREATE ACCOUNT"}
          </button>
        </form>

        <div className="divider-ascii mt-10 text-xs">// // // // // // // // // // // //</div>
        <p
          className="font-body text-white/70 text-xs md:text-sm mt-6 leading-relaxed max-w-md"
          data-testid="auth-disclaimer"
        >
          By entering, you confirm that you are <span className="text-acid font-mono">18 years of age or older</span>{" "}
          and that you accept the{" "}
          <button
            type="button"
            onClick={() => setRulesOpen(true)}
            data-testid="open-community-rules"
            className="text-acid underline underline-offset-2 hover:text-paper focus:outline-none focus:ring-1 focus:ring-acid font-medium"
          >
            NiteWire Community Rules
          </button>
          .
        </p>
      </div>

      <CommunityRulesModal open={rulesOpen} onOpenChange={setRulesOpen} />
    </div>
  );
}
